/**
 * Centralised AI pricing registry and cost estimation.
 *
 * RULE: model prices exist in exactly one place — this file. Adding a model or
 * changing a price must never require touching feature code, adapters, or the
 * telemetry layer.
 *
 * The registry is intentionally empty of vendor prices in Phase 1C. Phase 1D
 * registers OpenAI prices via `registerModelPricing`, which keeps the pricing
 * table co-located with the provider adapter that needs it.
 */

import type { AIProviderName, CostEstimate } from './types';
import type { NormalizedTokenUsage } from './usage';

/** Price definition for one model, expressed per million tokens. */
export interface ModelPricing {
  provider: AIProviderName;
  /** Provider-native model id. */
  model: string;
  /** Currency of the amounts below, ISO 4217. */
  currency: string;
  /** Cost per 1M input (prompt) tokens. */
  inputPerMillion: number;
  /** Cost per 1M output (completion) tokens. */
  outputPerMillion: number;
  /** Cost per 1M cached input tokens, when the provider discounts them. */
  cachedInputPerMillion?: number;
  /** Cost per 1M reasoning tokens, when billed separately from output. */
  reasoningPerMillion?: number;
  /** Flat cost per request (e.g. image generation), in major units. */
  perRequest?: number;
  /** Free-text note, e.g. effective date of the price list. */
  note?: string;
}

const key = (provider: AIProviderName, model: string) => `${provider}::${model}`;

const registry = new Map<string, ModelPricing>();

/** Register or replace pricing for a model. Called by provider modules. */
export function registerModelPricing(...entries: ModelPricing[]): void {
  for (const entry of entries) registry.set(key(entry.provider, entry.model), entry);
}

export function getModelPricing(
  provider: AIProviderName,
  model: string | undefined,
): ModelPricing | undefined {
  return model ? registry.get(key(provider, model)) : undefined;
}

export const listModelPricing = (): ModelPricing[] => Array.from(registry.values());

/** Test/bootstrap helper. */
export const resetModelPricing = (): void => registry.clear();

const MILLION = 1_000_000;

/**
 * Estimate the cost of a call. Returns `undefined` when the model has no
 * registered price — callers must treat cost as optional, never as zero.
 */
export function estimateCost(
  provider: AIProviderName,
  model: string | undefined,
  usage: NormalizedTokenUsage | undefined,
): CostEstimate | undefined {
  const pricing = getModelPricing(provider, model);
  if (!pricing) return undefined;

  let amount = pricing.perRequest ?? 0;

  if (usage) {
    const cached = usage.cachedTokens ?? 0;
    const billableInput = Math.max(0, usage.promptTokens - cached);
    const reasoning = usage.reasoningTokens ?? 0;
    const billableOutput = Math.max(
      0,
      usage.completionTokens - (pricing.reasoningPerMillion ? reasoning : 0),
    );

    amount += (billableInput / MILLION) * pricing.inputPerMillion;
    amount += (billableOutput / MILLION) * pricing.outputPerMillion;
    if (cached) {
      amount += (cached / MILLION) * (pricing.cachedInputPerMillion ?? pricing.inputPerMillion);
    }
    if (reasoning && pricing.reasoningPerMillion) {
      amount += (reasoning / MILLION) * pricing.reasoningPerMillion;
    }
  }

  return {
    currency: pricing.currency,
    // Sub-cent precision matters at scale; round to 6 decimals.
    amount: Math.round(amount * 1e6) / 1e6,
    estimated: true,
  };
}
